import { Button, Flex, Text, VStack } from '@chakra-ui/react';
import { CloseIcon, FileIcon } from 'assets/icons/v2';
import { CustomModal } from 'components/common/Modal/CustomModal';
import { CustomModalContent } from 'components/common/Modal/CustomModalContent';
import { FormInput } from 'components/ui/form/Input';
import { NovaIcon } from 'components/ui/novaIcon';
import { useCopyWorksheetFromTemplate } from 'hooks/api/backend/worksheets/useWorksheetTemplates';
import { useNovaToast } from 'hooks/utils/useNovaToast';
import { ReactElement, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ModalWorksheetTemplateCopyVariantProps, SharedModalProps } from 'types/Modal';
import { ToastVariant } from 'types/Toast';
import { ProtectedRoutes } from 'types/Routes';
import { NovaIconButton } from 'components/ui/NovaIconButton';

type Props = SharedModalProps & ModalWorksheetTemplateCopyVariantProps;

export const WorksheetTemplateCopyModal = ({
  handleClose,
  isShown,
  templateId,
  initialTitle,
  onCopied,
}: Props): ReactElement => {
  const defaultTitle = useMemo(() => {
    if (!initialTitle) return '';
    return `${initialTitle} (Kopie)`;
  }, [initialTitle]);

  const [copyName, setCopyName] = useState(defaultTitle);
  const { mutateAsync: copyWorksheetFromTemplate, isPending } = useCopyWorksheetFromTemplate();
  const { show: showToast } = useNovaToast();
  const navigate = useNavigate();

  const handleSubmit = async (): Promise<void> => {
    const title = copyName.trim();
    if (!title) return;

    const copiedWorksheet = await copyWorksheetFromTemplate({
      templateId,
      title,
    });

    onCopied?.(copiedWorksheet.id);
    showToast({
      variant: ToastVariant.SUCCESS,
      title: 'Kopie wurde erstellt',
      description: `${title} wurde erfolgreich zu deinen Arbeitsblättern hinzugefügt`,
      action: {
        label: 'Arbeitsblatt öffnen',
        onClick: () => navigate(`${ProtectedRoutes.WORKSHEET_DETAIL}/${copiedWorksheet.id}`),
      },
    });
    handleClose();
  };

  return (
    <CustomModal isOpen={isShown} onClose={handleClose} placement="center" hideHeader size="md">
      <CustomModalContent p="30px">
        <Flex direction="column" gap="50px">
          <VStack align="stretch" gap="20px">
            <Flex justify="flex-end">
              <NovaIconButton
                aria-label="Schließen"
                variant="primary"
                size="l"
                onClick={handleClose}
                icon={CloseIcon}
                iconProps={{ color: 'text.primary' }}
              />
            </Flex>

            <VStack align="stretch" gap="15px">
              <Text textStyle="h3.medium" color="text.primary">
                Kopie erstellen
              </Text>
              <Text textStyle="body.m.regular" color="text.tertiary">
                Speichert eine bearbeitbare Kopie in deinen Arbeitsblättern
              </Text>
            </VStack>

            <VStack align="stretch" gap="2xs">
              <FormInput
                value={copyName}
                onChange={(e) => setCopyName(e.target.value)}
                label="Name der Kopie"
                placeholder="Name der Kopie"
                leftElement={
                  <NovaIcon icon={FileIcon} size="s" color="icon.quaternary" aria-hidden />
                }
              />
              <Text textStyle="body.xs.regular" color="text.tertiary">
                Die Kopie wird in deinen Arbeitsblättern gespeichert
              </Text>
            </VStack>
          </VStack>

          <Flex gap="10px" justify="flex-end">
            <Button variant="tertiary" onClick={handleClose} disabled={isPending}>
              Abbrechen
            </Button>
            <Button
              variant="primary"
              onClick={async () => {
                await handleSubmit();
              }}
              disabled={isPending || !copyName.trim()}
            >
              Kopie speichern
            </Button>
          </Flex>
        </Flex>
      </CustomModalContent>
    </CustomModal>
  );
};
